import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportNonCashPayments } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const NonCashPaymentsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["non-cash-payments", startDateStr, endDateStr],
    queryFn: () =>
      GetSalesReportNonCashPayments({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const total = reportData.reduce(
      (sum: number, item: any) => sum + (item.amount || 0),
      0,
    );
    return [
      {
        title: "Total Digital Net Collections",
        value: formatLKR(total),
        colorClass: "text-cyan-700",
      },
      {
        title: "Total Transactions Audited",
        value: `${reportData.length} Payments`,
        colorClass: "text-slate-655",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Order No Reference",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">Order #{v}</span>
      ),
    },
    {
      title: "Payment Channel",
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      render: (v: string) => (
        <Tag
          color="cyan"
          className="rounded-full font-bold text-[10px] uppercase"
        >
          {v}
        </Tag>
      ),
    },
    {
      title: "Audit Note / Reference",
      dataIndex: "note",
      key: "note",
      render: (v: string) => (
        <span className="text-xs text-gray-500 italic">{v || "—"}</span>
      ),
    },
    {
      title: "Total Paid",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-black text-gray-800">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Card & Bank Payments"
      description="Audit digital collections including credit card swipes and bank transfers."
      icon={<Landmark className="w-6 h-6 text-cyan-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
